import { createQuery } from "@/api/utils/createQuery";
import { File, FileSource } from "./File.model";
import { LineageTree } from "./File.types";
import { QueryStringBuilder } from "@/utils";

export const getFile = createQuery<File, { fileId: string }>(
    async ({ api, router }) => {
        const response = await api.get(`/file/files/${router.params.fileId}`);
        return response;
    },
);

export const getFiles = createQuery<File[], { source?: FileSource }>(
    async ({ api, router, params }) => {
        const response = await api.get(
            `/file/files?max_page_size=999&company_id=in.("${router.params.companyId}")${params.source ? `&source=eq.${params.source}` : ""}`,
        );

        return response.items;
    },
);

export const getUploads = createQuery<File[]>(async ({ api, router }) => {
    const query = new QueryStringBuilder()
        .equals("company_id", router.params.companyId || "")
        .equals("source", FileSource.USER_UPLOADED as string)
        .equals("document_type", "raw")
        .add("max_page_size", "999")
        .build();
    const response = await api.get(`/file/files?${query}`);

    return response.items;
});

type FileInfo = { id: string; name: string };
type FileTree = { id: string; name: string; ancestors: FileTree[] };

const flattenFileTree = (tree: FileTree): FileInfo[] => {
    const result: FileInfo[] = [{ id: tree.id, name: tree.name }];

    if (tree.ancestors && tree.ancestors.length > 0) {
        tree.ancestors.forEach((ancestor) => {
            result.push(...flattenFileTree(ancestor));
        });
    }

    return result;
};

export const getAncestors = createQuery<
    { root: File; children: FileInfo[] },
    { fileId: string }
>(async ({ api, params }) => {
    const response: FileTree = await api.get(
        `/file/files/${params.fileId}/ancestors`,
    );

    const flattened = flattenFileTree(response).reverse();

    const root = flattened[0];

    const fileData = await api.get(`/file/files/${root.id}`);

    return {
        root: fileData,
        children: flattened.slice(1),
    };
});

const flattenLineageTree = (tree: LineageTree): FileInfo[] => {
    const result: FileInfo[] = [];

    if (tree.descendants && tree.descendants.length > 0) {
        tree.descendants.forEach((descendant) => {
            result.push({ id: descendant.id, name: descendant.name });
            result.push(...flattenLineageTree(descendant));
        });
    }

    return result.slice(1);
};

export const getDescendants = createQuery<
    LineageTree | FileInfo[],
    { fileId: string; flattened?: boolean }
>(async ({ api, params }) => {
    const response: LineageTree = await api.get(
        `/file/files/${params.fileId}/descendants`,
    );

    if (params.flattened) {
        return flattenLineageTree(response);
    }

    return response;
});
